import { setDoc, deleteDoc, doc, updateDoc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

import { ICL, Task, TaskComment } from 'shared/types';

import { firestore } from '../Common/Firebase';

const colName = 'Works';
const subColName = 'Tasks';
const subSubColName = 'Comments';
const getDocRef = (ICLId: ICL['id'], taskId: Task['id'], commentId: TaskComment['id']) =>
  doc(firestore, colName, ICLId, subColName, taskId, subSubColName, commentId);
const getColRef = (ICLId: ICL['id'], taskId: Task['id']) =>
  collection(firestore, colName, ICLId, subColName, taskId, subSubColName);

async function addComment(
  ICLId: ICL['id'],
  taskId: Task['id'],
  commentText: TaskComment['text'],
  name: TaskComment['name'],
) {
  const newId = uuidv4();

  const newComment = {
    id: newId,
    text: commentText,
    createdAt: new Date().getTime(),
    isViewed: false,
    name,
  };

  const docRef = getDocRef(ICLId, taskId, newId);

  await setDoc(docRef, newComment);
}

async function updateComment(
  ICLId: ICL['id'],
  taskId: Task['id'],
  commentId: TaskComment['id'],
  newData: Partial<TaskComment>,
) {
  const docRef = getDocRef(ICLId, taskId, commentId);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(docRef, newData as any);
}

async function removeComment(ICLId: ICL['id'], taskId: Task['id'], commentId: TaskComment['id']) {
  const docRef = getDocRef(ICLId, taskId, commentId);

  await deleteDoc(docRef);
}

interface SubscribeToCommentsParams {
  iclId: string;
  taskId: string;
  setComments: (tasks: TaskComment[]) => void;
}

function subscribeToComments({ iclId, taskId, setComments }: SubscribeToCommentsParams) {
  const subColRef = getColRef(iclId, taskId);
  const queryRef = query(subColRef, orderBy('createdAt'));

  return onSnapshot(queryRef, snapshot => {
    const comments: TaskComment[] = [];
    snapshot.forEach(doc => comments.push(doc.data() as TaskComment));
    setComments(comments);
  });
}

interface SubscribeToCommentParams {
  iclId: ICL['id'];
  taskId: Task['id'];
  commentId: TaskComment['id'];
  setComment: (ICL: TaskComment) => void;
}

function subscribeToComment({ iclId, taskId, commentId, setComment }: SubscribeToCommentParams) {
  const docRef = getDocRef(iclId, taskId, commentId);

  return onSnapshot(docRef, snapshot => {
    setComment(snapshot.data() as TaskComment);
  });
}

export const commentApi = {
  addComment,
  removeComment,
  updateComment,
  subscribeToComments,
  subscribeToComment,
};
